import React, { useMemo } from 'react';
import { Typography, Form, Input, Select, Row, Col } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import CrudPage from '../../../components/common/CrudPage';
import objectService from '../../../services/cms/objectServiceRequest';
import objectTypeService from '../../../services/cms/objectTypeServiceRequest';
import { safeParseColumns, safeParseObject } from '../../../utils/utility';

const { Text } = Typography;

const QuanLyDoiTuong = () => {
  // Thể loại (object_type): dùng cho select "Thể loại", map tên cột LOẠI và sinh ô nhập động theo cột của thể loại.
  // Dùng getList thay listActive: endpoint list-active-* không dùng được từ trình duyệt (lỗi CORS trên staging)
  // nên select trơ ra rỗng — cùng lý do đã đổi ở trang Thể loại / Danh mục sự kiện.
  const { data: typesData } = useQuery({
    queryKey: ['allObjectTypes'],
    queryFn: () => objectTypeService.getList({ page: 1, limit: 1000 }),
  });
  const objectTypes = useMemo(() => {
    if (Array.isArray(typesData)) return typesData;
    return typesData?.data?.data || (Array.isArray(typesData?.data) ? typesData.data : []) || [];
  }, [typesData]);
  const typeById = useMemo(() => {
    const map = {};
    objectTypes.forEach((type) => {
      map[type.id] = type;
    });
    return map;
  }, [objectTypes]);

  // Quốc gia kèm mảng thành phố — dropdown Thành phố load theo quốc gia đang chọn
  const { data: countriesData } = useQuery({
    queryKey: ['allCountries'],
    queryFn: () => objectService.listCountries(),
  });
  const countries = useMemo(() => (Array.isArray(countriesData) ? countriesData : []), [countriesData]);

  const filterFields = useMemo(
    () => [
      {
        label: 'Thể loại',
        value: 'type_id',
        options: [
          { label: 'Tất cả thể loại', value: '' },
          ...objectTypes.map((type) => ({ label: type.name, value: String(type.id) })),
        ],
      },
    ],
    [objectTypes]
  );

  const formFields = useMemo(
    () => [
      {
        name: 'title',
        label: 'Tiêu đề',
        type: 'input',
        rules: [{ required: true, message: 'Vui lòng nhập tiêu đề!' }],
        placeholder: 'Tiêu đề đối tượng',
      },
      {
        name: 'type_id',
        label: 'Thể loại',
        type: 'select',
        options: objectTypes.map((type) => ({ label: `[${type.id}] ${type.name}`, value: type.id })),
        placeholder: 'Chọn thể loại',
        propsInput: { showSearch: true, allowClear: true, optionFilterProp: 'label' },
      },
      {
        // Ô nhập động theo cột của thể loại đang chọn — giá trị đóng gói vào metadata khi lưu
        type: 'html',
        content: (
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.type_id !== cur.type_id}>
            {({ getFieldValue }) => {
              const cols = safeParseColumns(typeById[getFieldValue('type_id')]?.columns);
              if (!cols.length) return null;
              return (
                <Row gutter={16}>
                  {cols.map((col) => (
                    <Col span={12} key={col}>
                      {/* Tên cột có thể chứa dấu cách nên bắt buộc dùng name dạng mảng */}
                      <Form.Item name={['metadata_fields', col]} label={col}>
                        <Input placeholder={col} allowClear />
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              );
            }}
          </Form.Item>
        ),
      },
      { name: 'address', label: 'Địa chỉ', type: 'input', placeholder: 'Địa chỉ' },
      {
        // Quốc gia/Thành phố phụ thuộc nhau nên render trong một block để cascade
        type: 'html',
        content: (
          <Form.Item noStyle shouldUpdate={(prev, cur) => prev.country !== cur.country}>
            {(form) => {
              const selectedCountry = countries.find((country) => country.key === form.getFieldValue('country'));
              return (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="country" label="Quốc gia">
                      <Select
                        options={countries.map((country) => ({ value: country.key, label: country.name }))}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="Chọn quốc gia"
                        onChange={() => form.setFieldsValue({ state: undefined })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="state" label="Thành phố">
                      <Select
                        options={(selectedCountry?.state || []).map((city) => ({ value: city, label: city }))}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        placeholder="Chọn thành phố"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        ),
      },
      {
        name: 'location_latitude',
        label: 'Vị trí vĩ độ',
        type: 'input',
        span: 12,
        placeholder: 'Ví dụ: 21.028511',
        rules: [{ pattern: /^-?\d+(\.\d+)?$/, message: 'Tọa độ không hợp lệ (dùng dấu chấm thập phân)' }],
      },
      {
        name: 'location_longitude',
        label: 'Vị trí kinh độ',
        type: 'input',
        span: 12,
        placeholder: 'Ví dụ: 105.804817',
        rules: [{ pattern: /^-?\d+(\.\d+)?$/, message: 'Tọa độ không hợp lệ (dùng dấu chấm thập phân)' }],
      },
      { name: 'description', label: 'Mô tả', type: 'textarea', placeholder: 'Mô tả đối tượng' },
      { name: 'notification_default', label: 'Thông báo mặc định', type: 'textarea', placeholder: 'Thông báo mặc định (JSON)', propsInput: { rows: 2 } },
      { name: 'is_place', type: 'checkbox', labelCheckbox: 'Là Place', span: 12 },
      { name: 'is_collapse', type: 'checkbox', labelCheckbox: 'Thu gọn trên lịch', span: 12 },
    ],
    [objectTypes, countries, typeById]
  );

  const columns = useMemo(
    () => [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
      { title: 'TÊN ĐỐI TƯỢNG', dataIndex: 'title', key: 'title', render: (text) => <Text strong>{text}</Text> },
      {
        title: 'THỂ LOẠI',
        dataIndex: 'type_id',
        key: 'type_id',
        width: 140,
        render: (val) => (val ? typeById[val]?.name || `[${val}]` : '-'),
      },
      {
        title: 'CỘT',
        dataIndex: 'metadata',
        key: 'metadata',
        width: 220,
        render: (raw) => {
          const entries = Object.entries(safeParseObject(raw));
          return entries.length ? (
            <div>
              {entries.map(([key, value]) => (
                <div key={key}>
                  {key}: {String(value ?? '')}
                </div>
              ))}
            </div>
          ) : (
            '-'
          );
        },
      },
      { title: 'MÔ TẢ', dataIndex: 'description', key: 'description', width: 180, ellipsis: true },
      { title: 'ĐỊA CHỈ', dataIndex: 'address', key: 'address', width: 180, ellipsis: true },
      { title: 'NGÀY TẠO', dataIndex: 'create_time', key: 'create_time', width: 140, render: (time) => (time ? dayjs(time).format('DD/MM/YYYY HH:mm') : '-') },
    ],
    [typeById]
  );

  const mapDataToForm = (record) => ({
    metadata_fields: safeParseObject(record.metadata),
    is_place: record.is_place === 1,
    is_collapse: record.is_collapse === 1,
    type_id: record.type_id || undefined,
    country: record.country || undefined,
    state: record.state || undefined,
  });

  const toNumberOrNull = (value) => {
    if (value === '' || value === undefined || value === null) return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
  };

  const mapFormToData = (values) => {
    // Đóng gói giá trị các ô động vào metadata theo đúng bộ cột của thể loại đang chọn (key = tên cột gốc).
    // Nếu type_id có nhưng không tra được thể loại (danh sách chưa load / thể loại đã bị xóa) thì
    // KHÔNG gửi metadata để tránh ghi đè "{}" làm mất dữ liệu — undefined sẽ bị loại khi serialize
    const selectedType = typeById[values.type_id];
    const isTypeKnown = !values.type_id || Boolean(selectedType);
    const cols = safeParseColumns(selectedType?.columns);
    const metadata = {};
    cols.forEach((col) => {
      metadata[col] = values.metadata_fields?.[col] ?? '';
    });
    return {
      metadata: isTypeKnown ? JSON.stringify(metadata) : undefined,
      metadata_fields: undefined, // key ảo, validator API không nhận — undefined sẽ bị loại khi serialize
      type_id: values.type_id ?? null, // gửi null khi bỏ chọn để clear được trên DB (nếu không, key bị drop và DB giữ type cũ)
      is_place: values.is_place ? 1 : 0,
      is_collapse: values.is_collapse ? 1 : 0,
      // Joi.number() reject chuỗi rỗng nên quy về null khi bỏ trống
      location_latitude: toNumberOrNull(values.location_latitude),
      location_longitude: toNumberOrNull(values.location_longitude),
      // Gửi chuỗi rỗng (không phải undefined) để clear được giá trị cũ khi update
      country: values.country || '',
      state: values.state || '',
    };
  };

  return (
    <CrudPage
      title="QUẢN LÝ ĐỐI TƯỢNG"
      subTitle="Quản lý danh sách đối tượng trong hệ thống"
      itemsName="Đối tượng"
      queryKey="objectList"
      service={objectService}
      columns={columns}
      formFields={formFields}
      filterFields={filterFields}
      searchPlaceholder="Tìm kiếm theo tên đối tượng..."
      addButtonLabel="Thêm đối tượng"
      formConfig={{ width: 900 }}
      mapDataToForm={mapDataToForm}
      mapFormToData={mapFormToData}
    />
  );
};

export default QuanLyDoiTuong;
